import { Component } from "@theme/component";
import { mediaHoverFine, prefersReducedMotion } from "@theme/utilities";

const DESKTOP_MEDIA_QUERY = window.matchMedia("(min-width: 768px)");

// A card counts as the active mobile slide once this much of it is visible;
// the DeferredMedia observer (0.1) would also play the peek of the next slide.
const ACTIVE_VISIBLE_RATIO = 0.6;

/** @type {WeakMap<Element, number>} pending hover-out autoplay resume per carousel */
const autoplayResumeTimers = new WeakMap();

class VideoCard extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #hoverIntentTimer = null;

  /** @type {boolean | undefined} */
  #wantsAutoplay;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Respect reduced motion: do not autoplay on hover/focus.
    if (prefersReducedMotion()) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Desktop shows multiple cards side by side: only the first card keeps autoplay.
    // Mobile: a visibility observer plays only the dominant slide while swiping.
    this.#applyAutoplayScope();
    DESKTOP_MEDIA_QUERY.addEventListener("change", this.#applyAutoplayScope, { signal });

    // Only bind hover autoplay for pointer-fine devices.
    if (!mediaHoverFine()) return;

    this.addEventListener("mouseenter", this.#onEnter, { signal });
    this.addEventListener("mouseleave", this.#onLeave, { signal });
    this.addEventListener("focusin", this.#onEnter, { signal });
    this.addEventListener("focusout", this.#onFocusOut, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearHoverIntent();
    this.#disconnectVisibilityObserver();
  }

  get deferredMedia() {
    return this.querySelector("deferred-media");
  }

  get content() {
    return this.querySelector(".video-card__content");
  }

  #carouselRoot() {
    return this.closest("flex-carousel") || this.closest(".shopify-section") || document.body;
  }

  #isFirstCard() {
    return this.#carouselRoot().querySelector("video-card") === this;
  }

  #applyAutoplayScope = () => {
    const dm = this.deferredMedia;
    if (!dm) return;

    this.#wantsAutoplay ??= dm.hasAttribute("data-autoplay");
    if (!this.#wantsAutoplay) return;

    if (DESKTOP_MEDIA_QUERY.matches) {
      this.#disconnectVisibilityObserver();
      if (this.#isFirstCard()) {
        this.#enableDeferredAutoplay(dm);
      } else {
        this.#disableDeferredAutoplay(dm);
      }
    } else {
      this.#disableDeferredAutoplay(dm);
      this.#observeVisibility();
    }
  };

  #enableDeferredAutoplay(dm) {
    if (dm.hasAttribute("data-autoplay")) return;
    dm.removeAttribute("data-autoplay-blocked");
    dm.setAttribute("data-autoplay", "");
  }

  #disableDeferredAutoplay(dm) {
    if (!dm.hasAttribute("data-autoplay")) return;
    // Blocked flag keeps DeferredMedia's attributeChangedCallback from replaying on removal.
    dm.setAttribute("data-autoplay-blocked", "");
    dm.removeAttribute("data-autoplay");
    dm.pauseMedia?.();
  }

  #observeVisibility() {
    if (this.#visibilityObserver) return;

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= ACTIVE_VISIBLE_RATIO) {
            void this.#play();
          } else {
            this.deferredMedia?.pauseMedia?.();
          }
        }
      },
      { threshold: [ACTIVE_VISIBLE_RATIO] }
    );
    this.#visibilityObserver.observe(this);
  }

  #disconnectVisibilityObserver() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  #pauseSiblings() {
    const root = this.#carouselRoot();
    window.clearTimeout(autoplayResumeTimers.get(root));

    for (const card of root.querySelectorAll("video-card")) {
      if (card === this) continue;
      card.querySelector("deferred-media")?.pauseMedia?.();
    }
  }

  #scheduleAutoplayResume() {
    const root = this.#carouselRoot();
    window.clearTimeout(autoplayResumeTimers.get(root));

    const timer = window.setTimeout(() => {
      const dm = root.querySelector("deferred-media[data-autoplay]:not([data-autoplay-blocked])");
      if (!dm || dm === this.deferredMedia) return;

      // Only resume when the autoplay card is actually on screen.
      const rect = dm.getBoundingClientRect();
      const inView = rect.right > 0 && rect.left < window.innerWidth && rect.bottom > 0 && rect.top < window.innerHeight;
      if (!inView) return;

      dm.playMedia?.();
    }, 200);
    autoplayResumeTimers.set(root, timer);
  }

  #clearHoverIntent() {
    if (this.#hoverIntentTimer) {
      window.clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
    }
  }

  #onEnter = () => {
    this.#clearHoverIntent();
    window.clearTimeout(autoplayResumeTimers.get(this.#carouselRoot()));

    // Small delay avoids accidental scrubs when moving pointer across a grid.
    this.#hoverIntentTimer = window.setTimeout(() => {
      this.#setActive(true);
      this.#pauseSiblings();
      void this.#play();
    }, 120);
  };

  #onLeave = () => {
    this.#clearHoverIntent();
    this.#setActive(false);
    this.#pause();
    this.#scheduleAutoplayResume();
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof HTMLElement && this.contains(next)) return;
    this.#onLeave();
  };

  #setActive(isActive) {
    this.classList.toggle("is-hovering", isActive);
    this.content?.classList.toggle("is-hidden", isActive);
  }

  async #play() {
    const dm = this.deferredMedia;
    if (!dm) return;

    // If poster exists and media isn't loaded, load + play.
    if (!dm.hasAttribute("data-media-loaded")) {
      await dm.showDeferredMedia?.();
      return;
    }

    await dm.playMedia?.();
  }

  #pause() {
    const dm = this.deferredMedia;
    if (!dm) return;
    // The autoplay card is viewport-controlled (IntersectionObserver); hover-out must not stop it.
    if (dm.hasAttribute("data-autoplay")) return;
    dm.pauseMedia?.();
  }
}

if (!customElements.get("video-card")) {
  customElements.define("video-card", VideoCard);
}
